/**
 * @Copyright (c) Microsoft Corporation.  All rights reserved.
 *
 * @file SPModuleLoader.ts
 */

require('set-webpack-public-path!');

import * as Combokeys from 'combokeys';

import {
  IClientSideComponentManifest,

  IBundleReference,
  IStandardLibraryModule,
  standardLibraryModules
} from '@microsoft/sp-module-interfaces';

// Keep DeveloperToolsLoader at the top to keep the chunks ordered correctly
import DeveloperToolsLoader from './DeveloperTools/DeveloperToolsLoader';
import { IDeveloperToolsTab } from './DeveloperTools/IDeveloperToolsTab';
import SystemJsWrapper from './SystemJsWrapper';
import ManifestStore from './stores/ManifestStore';
import ModuleStore from './stores/ModuleStore';
import DebugManifestProvider from './DebugManifestProvider';
import SPFlight from './staticLinks/SPFlight';

import stringFormat from './utilities/stringFormat';
import strings from './SPModuleLoader.resx';

const basePlatformPackageName: string = '@microsoft/sp-client-preview';

export interface IPartialSpPageContextInfo {
  currentUICultureName: string;
  ExpFeatures: number[];
}

export interface IApplicationPreloadedData {
  clientSideApplicationId: string;
  clientSideApplicationManifest: IClientSideComponentManifest;
  manifests: IClientSideComponentManifest[];
  spPageContextInfo: IPartialSpPageContextInfo;
}

let _allowDebug: boolean = undefined;
export function ensureDebugAllowed(ask: boolean = true): boolean {
  if (!ask || _allowDebug !== undefined) {
    return !!_allowDebug;
  } else {
    // Inform the user that they might be loading debug manifests
    _allowDebug = confirm(strings.debugManifestLoadingWarning);
    return _allowDebug;
  }
}

/**
 * The global module loader for SP client-side applications and webparts.
 *
 * This is an API that lives in a global variable on the window, similar to how systemJS works.
 *
 * @public
 */
export default class SPModuleLoader {
  private static _headElement: HTMLHeadElement;

  // Initialization

  constructor() {
    // Watching for hotkeys on the whole document.
    const fullWindowCombokeys: Combokeys.Combokeys = new Combokeys(document.documentElement);

    // Bind the debugger to "ctrl+shift+`"
    fullWindowCombokeys.bind('ctrl+shift+`', (): void => DeveloperToolsLoader.instance.toggleDeveloperTools());

    if (!SPModuleLoader._headElement) {
      SPModuleLoader._headElement = document.getElementsByTagName('head')[0];
    }
  }

  public initialize(preloadedData: IApplicationPreloadedData): Promise<void> {
    return new Promise<void>((resolve: () => void) => {
        ManifestStore.instance.populateManifests(preloadedData);
        this._loadDebugManifests().then(() => {
            if (preloadedData && preloadedData.spPageContextInfo) {
              SystemJsWrapper.instance.currentLocale = preloadedData.spPageContextInfo.currentUICultureName;
            }

            resolve();
          });
    });
  }

  /**
   * This is called by the page scripts to start loading the framework. Do not call it from your own code.
   */
  /* tslint:disable:no-any */
  public start(preloadedData: IApplicationPreloadedData): Promise<any> {
    if (preloadedData && preloadedData.spPageContextInfo) {
      SPFlight.initialize(preloadedData.spPageContextInfo.ExpFeatures);
    }

    const result: Promise<any> = new Promise<any>((resolve: (value: any) => void,
                                                   reject: (error: Error) => void): void => {
  /* tslint:enable:no-any */
      this.initialize(preloadedData).then(() => {
        if (preloadedData) {
          const applicationManifestId: string = preloadedData.clientSideApplicationId
            ? preloadedData.clientSideApplicationId
            : (preloadedData.clientSideApplicationManifest
              ? preloadedData.clientSideApplicationManifest.id
              : undefined);

          const manifest: IClientSideComponentManifest = ManifestStore.instance.getManifestById(applicationManifestId);
          if (manifest) {
            const platformModule: IStandardLibraryModule = standardLibraryModules[basePlatformPackageName];

            let loadingComplete: boolean = false;
            Promise.all([
              // Load the platform in addition to the application
              this.loadStandardLibraryModule(platformModule, basePlatformPackageName),
              SystemJsWrapper.instance.loadManifestModules(manifest.id, manifest.loaderConfig)
            ])
              .then((modules: any[]) => { // tslint:disable-line:no-any
                loadingComplete = true;

                const platform: any = modules[0]; // tslint:disable-line:no-any
                const application: {} = modules[1];

                if (!platform || !application) {
                  reject(new Error(stringFormat(strings.platformOrApplicationFailedToLoadError,
                                                basePlatformPackageName,
                                                manifest.loaderConfig.entryModuleId)));
                } else {
                  platform._spShell.start(application, preloadedData)
                    .then((application: any) => resolve(application)) // tslint:disable-line:no-any
                    .catch((error: Error) => {
                      console.error(stringFormat(strings.applicationFailedToInitializeError, error));
                      reject(error);
                  });
                }

                // @todo VSO #210443: preload WebPart manifests
              }).catch((e) => {
                if (!loadingComplete) {
                  console.error(stringFormat(strings.applicationFailedToLoadError,
                                             manifest.loaderConfig.entryModuleId,
                                             e));
                }

                reject(e);
              });
          } else {
            reject(new Error(stringFormat(strings.invalidManifestError, applicationManifestId)));
          }
        } else {
          reject(new Error(strings.invaliPreloadedDataError));
        }
      });
    });

    result.catch((error: Error) => {
      // Log and re-throw errors
      console.error(error);
      throw error;
    });

    return result;
  }

  // Public API

  /**
   * Given a URL, load a script.
   *
   * @param scriptSource      - The script URL.
   * @param globalExportsName - If the script isn't an AMD mdoule and loads a global member on the page,
   *                              specify the global member's name.
   * @returns                   A promise containing the loaded module.
   */
  public loadScript(scriptSource: string, globalExportsName?: string): Promise<any> { // tslint:disable-line:no-any
    return SystemJsWrapper.instance.loadScript(scriptSource, globalExportsName);
  }

  /**
   * Loads a module from a manifest.
   *
   * @param manifest - Manifest of the module to load.
   * @returns          A promise containing the loaded module.
   */
  public loadModule(manifest: IClientSideComponentManifest): Promise<any> { // tslint:disable-line:no-any
    return this._internalLoadModule(manifest);
  }

  /**
   * Resolve a component id and version, and load it.
   *
   * @param id      - The id of the component to load.
   * @param version - The version of the component to load. If version is not defined, the method
   *                    will load the last version of the component.
   * @returns         A promise containing the loaded module.
   */
  public loadModuleById(id: string, version?: string): Promise<any> { // tslint:disable-line:no-any
    return this._internalLoadModule([{
      id: id
    }]);
  }

  /**
   * Loads additional manifests into the manifest store, updating existing manifests.
   *
   * @param manifests         - The manifests to load into the store.
   * @param overwriteExisting - If true, overwrite the stored manifest with the provided ones
   */
  public importManifests(manifests: IClientSideComponentManifest[], overwriteExisting: boolean = false): void {
    ManifestStore.instance.importManifests(manifests, overwriteExisting);
  }

  /**
   * Get all registered manifests.
   *
   * @returns All registered manifests.
   */
  public getManifests(): IClientSideComponentManifest[] {
    return ManifestStore.instance.getAllManifests();
  }

  /**
   * Inserts a <link ... /> tag for a stylesheet.
   *
   * @param url - The CSS file URL.
   */
  public loadCss(url: string): void {
    const linkTag: HTMLLinkElement = document.createElement('link');
    linkTag.rel = 'stylesheet';
    linkTag.type = 'text/css';
    linkTag.href = url;

    SPModuleLoader._headElement.appendChild(linkTag);
  }

  // Internal methods

  /**
   * If manifests from localhost haven't already been loaded, force them to load without prompting the user.
   *
   * @returns A promise that is fulfilled when the manifests are loaded.
   *
   * @internal
   */
  public forceLocalManifests(): Promise<void> {
    return this._loadDebugManifests(true, true, ['https://localhost:4321/temp/manifests.js',
                                                 'http://localhost:4321/temp/manifests.js']);
  }

  /**
   * Get the component manifest by id.
   *
   * @param id - GUID id of the component.
   *
   * @internal
   */
  public getManifestById(id: string, version?: string): IClientSideComponentManifest {
    return ManifestStore.instance.getManifestById(id);
  }

  /**
   * Registers a tab in the developer tools. The developer tools are invoked by pressing "CTRL+SHIFT+~".
   *
   * @param developerToolsTab - The tab definition.
   *
   * @internal
   */
  public registerDeveloperToolsTab(developerToolsTab: IDeveloperToolsTab): void {
    DeveloperToolsLoader.instance.registerDeveloperToolsTab(developerToolsTab);
  }

  // Methods to be deprecated

  /**
   * If debug manfiests haven't already been loaded, force them to load without prompting the user.
   *
   * @returns A promise that is fulfilled when the manifests are loaded.
   *
   * @deprecated
   */
  public forceLoadDebugManifests(): Promise<void> {
    return this.forceLocalManifests();
  }

  /**
   * Load a manifest, or resolve a manifest ID and load it.
   *
   * @param idOrManifest - The ID of a manifest, the manifest payload, or an array of IDs. In the case of an array
   *                        of IDs, all of the IDs are considered equally valid, so pick the first one that's resolvable
   *                        to a manifest.
   * @returns A promse containing the loaded module.
   *
   * @deprecated
   */
  public loadManifestModules(idOrManifest: string | IClientSideComponentManifest):
      Promise<any> { // tslint:disable-line:no-any
    if (typeof idOrManifest === 'string') {
      return this.loadModuleById(idOrManifest as string);
    } else {
      return this.loadModule(idOrManifest as IClientSideComponentManifest);
    }
  }

  /**
   * Load a standard library manifest.
   *
   * @param standardLibraryModule - The standard library module object.
   * @returns A promse containing the loaded module.
   */
  public loadStandardLibraryModule(standardLibraryModule: IStandardLibraryModule,
                                   moduleName: string): Promise<any> { // tslint:disable-line:no-any
    let resultPromise: Promise<any>; // tslint:disable-line:no-any
    if (NPM_BUILD) {
      // Special case for NPM modules to resolve React, React-DOM, and Flux
      resultPromise = ModuleStore.instance.npmLoadPackage(moduleName);
    }

    if (!NPM_BUILD || !resultPromise) {
      const references: IBundleReference[] =
        [standardLibraryModule.bundleReference].concat(standardLibraryModule.aliases || []);

      resultPromise = this._internalLoadModule(references);
    }

    return resultPromise.then((module: any) => { // tslint:disable-line:no-any
      ModuleStore.instance.storeModuleById(moduleName, module);
      return module;
    });
  }

  private _loadDebugManifests(noPrompt: boolean = false,
                              noCache: boolean = false,
                              alternativeManifestsFiles?: string[]): Promise<void> {
    if (noPrompt) {
      _allowDebug = true;
    }

    return DebugManifestProvider.instance.getDebugManifests(noCache, alternativeManifestsFiles).then(
      (debugManifests: { [id: string]: IClientSideComponentManifest }) => {
        ManifestStore.instance.populateDebugManifests(debugManifests);
        ensureDebugAllowed(debugManifests && Object.keys(debugManifests).length > 0);
      });
  }

  private _internalLoadModule(referenceOrManifest: IBundleReference[] | IClientSideComponentManifest):
      Promise<any> { // tslint:disable-line:no-any
    const references: IBundleReference[] = referenceOrManifest as IBundleReference[];
    let manifest: IClientSideComponentManifest;
    let entryName: string;
    if (references.length) {
      // We have an array of manifests, so find the first one that matches
      for (const reference of references) {
        manifest = ManifestStore.instance.getManifestById(reference.id);
        if (manifest) {
          entryName = reference.entryName;
          break;
        }
      }
    } else {
      manifest = referenceOrManifest as IClientSideComponentManifest;
    }

    if (manifest) {
      return SystemJsWrapper.instance.loadManifestModules(manifest.id, manifest.loaderConfig, entryName);
    } else {
      throw new Error(stringFormat(strings.manifestNotFoundError, JSON.stringify(referenceOrManifest)));
    }
  }
}



// WEBPACK FOOTER //
// /src/SPModuleLoader.ts


// WEBPACK FOOTER //
// webpack:///src/SPModuleLoader.ts